/** Storage-only owner snapshots. Callers authorize the owner, retire credentials
 * before cleanup, and retain unsaved work when a revision conflict is reported. */
export interface OwnedSnapshot {
  readonly version: 1
  readonly ownerId: string
  readonly generation: string | null
  readonly revision: string | null
  readonly value: string | null
}
/** A local recovery copy is evidence only; it must never be replayed as a queue. */
export interface SnapshotRecoveryCopy {
  readonly version: 1
  readonly ownerId: string
  readonly generation: string | null
  readonly operationId: string
  readonly createdAt: number
  readonly base: string | null
  readonly candidate: string | null
}
export class SnapshotStorageError extends Error {
  constructor() {
    super(
      'Saved changes storage is unavailable. Keep the original changes for recovery.',
    )
    this.name = 'SnapshotStorageError'
  }
}
export class SnapshotConflictError extends Error {
  constructor() {
    super(
      'Another window changed the saved work. Keep your changes and reload its recovery state before continuing.',
    )
    this.name = 'SnapshotConflictError'
  }
}
export class SnapshotOwnerRemovedError extends Error {
  constructor() {
    super(
      'This account was removed. Its old saved-work snapshot cannot be used.',
    )
    this.name = 'SnapshotOwnerRemovedError'
  }
}
const identifier = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0 && value.length <= 128
function decode(value: unknown, ownerId: string): OwnedSnapshot {
  if (value === undefined)
    return Object.freeze({
      version: 1,
      ownerId,
      generation: null,
      revision: null,
      value: null,
    })
  if (!value || typeof value !== 'object') throw new SnapshotStorageError()
  const row = value as Record<string, unknown>
  if (
    Object.keys(row).length !== 5 ||
    row.version !== 1 ||
    row.ownerId !== ownerId ||
    !(row.generation === null || identifier(row.generation)) ||
    !identifier(row.revision) ||
    !(row.value === null || typeof row.value === 'string')
  )
    throw new SnapshotStorageError()
  return Object.freeze({
    version: 1,
    ownerId,
    generation: row.generation as string | null,
    revision: row.revision,
    value: row.value as string | null,
  })
}
function validExpected(expected: OwnedSnapshot) {
  if (
    !identifier(expected.ownerId) ||
    expected.version !== 1 ||
    !(expected.generation === null || identifier(expected.generation)) ||
    !(expected.revision === null || identifier(expected.revision)) ||
    !(expected.value === null || typeof expected.value === 'string') ||
    (expected.revision === null &&
      (expected.value !== null || expected.generation !== null))
  )
    throw new SnapshotStorageError()
}

function decodeRecovery(
  value: unknown,
  owner: string,
  operation: string,
): SnapshotRecoveryCopy {
  if (!value || typeof value !== 'object') throw new SnapshotStorageError()
  const row = value as Record<string, unknown>
  if (
    Object.keys(row).length !== 7 ||
    row.version !== 1 ||
    row.ownerId !== owner ||
    row.operationId !== operation ||
    !identifier(operation) ||
    !(row.generation === null || identifier(row.generation)) ||
    typeof row.createdAt !== 'number' ||
    !Number.isSafeInteger(row.createdAt) ||
    row.createdAt < 0 ||
    !(row.base === null || typeof row.base === 'string') ||
    !(row.candidate === null || typeof row.candidate === 'string')
  )
    throw new SnapshotStorageError()
  return Object.freeze({
    version: 1,
    ownerId: owner,
    generation: row.generation as string | null,
    operationId: operation,
    createdAt: row.createdAt,
    base: row.base as string | null,
    candidate: row.candidate as string | null,
  })
}
function recoveryKey(key: IDBValidKey, owner: string): key is string[] {
  return (
    Array.isArray(key) &&
    key.length === 3 &&
    key[0] === 'recovery-copy' &&
    key[1] === owner &&
    typeof key[2] === 'string'
  )
}
function clearRecoveryCopies(
  store: IDBObjectStore,
  owner: string,
  done: () => void,
) {
  const cursor = store.openCursor()
  cursor.onsuccess = () => {
    const row = cursor.result
    if (!row) {
      done()
      return
    }
    if (recoveryKey(row.key, owner)) row.delete()
    row.continue()
  }
}

/** A write ID must be unique for one immutable candidate and reused for retries.
 * This store intentionally does not merge competing ledger states or replay work. */
export function createOwnedSnapshotStore(
  getFactory: () => IDBFactory = () => window.indexedDB,
  databaseName = 'scrambl-owned-offline-snapshots-v1',
) {
  let connection: Promise<IDBDatabase> | undefined
  const ownerKey = (owner: string) => ['snapshot', owner]
  const deletedKey = (owner: string) => ['deleted-owner', owner]
  const forgottenKey = (owner: string, operation: string) => [
    'forgotten-owner',
    owner,
    operation,
  ]
  function open(): Promise<IDBDatabase> {
    if (connection) return connection
    const pending = new Promise<IDBDatabase>((resolve, reject) => {
      let rejected = false
      const request = getFactory().open(databaseName, 2)
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains('snapshots'))
          request.result.createObjectStore('snapshots')
      }
      request.onerror = request.onblocked = () => {
        rejected = true
        reject(new SnapshotStorageError())
      }
      request.onsuccess = () => {
        const db = request.result
        if (rejected) {
          db.close()
          return
        }
        db.onclose = () => {
          connection = undefined
        }
        db.onversionchange = () => {
          db.close()
          connection = undefined
        }
        resolve(db)
      }
    })
    connection = pending
    void pending.catch(() => {
      if (connection === pending) connection = undefined
    })
    return pending
  }
  async function transaction<T>(
    owner: string,
    mode: IDBTransactionMode,
    run: (
      store: IDBObjectStore,
      raw: unknown,
      done: (value: T) => void,
      fail: (error: Error) => void,
    ) => void,
    allowDeleted = false,
  ): Promise<T> {
    if (!identifier(owner)) throw new SnapshotStorageError()
    try {
      const db = await open()
      return await new Promise<T>((resolve, reject) => {
        const tx = db.transaction(
          'snapshots',
          mode,
          mode === 'readwrite' ? { durability: 'strict' } : undefined,
        )
        const store = tx.objectStore('snapshots')
        let value: T
        let supplied = false
        let failure: Error | undefined
        const fail = (error: Error) => {
          failure = error
          tx.abort()
        }
        tx.oncomplete = () =>
          supplied ? resolve(value) : reject(new SnapshotStorageError())
        tx.onabort = () => reject(failure ?? new SnapshotStorageError())
        tx.onerror = () => reject(new SnapshotStorageError())
        const read = () => {
          const request = store.get(ownerKey(owner))
          request.onsuccess = () => {
            try {
              run(
                store,
                request.result,
                result => {
                  value = result
                  supplied = true
                },
                fail,
              )
            } catch (error) {
              fail(error instanceof Error ? error : new SnapshotStorageError())
            }
          }
        }
        const deleted = store.get(deletedKey(owner))
        deleted.onsuccess = () => {
          if (!allowDeleted && deleted.result !== undefined)
            fail(new SnapshotOwnerRemovedError())
          else read()
        }
      })
    } catch (error) {
      if (
        error instanceof SnapshotConflictError ||
        error instanceof SnapshotOwnerRemovedError ||
        error instanceof SnapshotStorageError
      )
        throw error
      throw new SnapshotStorageError()
    }
  }
  return {
    read(ownerId: string): Promise<OwnedSnapshot> {
      return transaction(ownerId, 'readonly', (_store, raw, done) =>
        done(decode(raw, ownerId)),
      )
    },
    write(
      expected: OwnedSnapshot,
      value: string | null,
      operationId: string,
    ): Promise<OwnedSnapshot> {
      validExpected(expected)
      if (
        !identifier(operationId) ||
        operationId === expected.revision ||
        !(value === null || typeof value === 'string')
      )
        throw new SnapshotStorageError()
      return transaction(expected.ownerId, 'readwrite', (store, raw, done) => {
        const current = decode(raw, expected.ownerId)
        if (current.generation !== expected.generation)
          throw new SnapshotOwnerRemovedError()
        if (current.revision === operationId) {
          if (current.value !== value) throw new SnapshotConflictError()
          done(current)
          return
        }
        if (
          current.revision !== expected.revision ||
          current.value !== expected.value
        )
          throw new SnapshotConflictError()
        const next: OwnedSnapshot = Object.freeze({
          ...current,
          revision: operationId,
          value,
        })
        store.put(next, ownerKey(expected.ownerId))
        done(next)
      })
    },
    retainRecovery(
      expected: OwnedSnapshot,
      value: SnapshotRecoveryCopy,
    ): Promise<void> {
      validExpected(expected)
      const copy = decodeRecovery(value, expected.ownerId, value.operationId)
      return transaction(
        expected.ownerId,
        'readwrite',
        (store, raw, done, fail) => {
          if (
            decode(raw, expected.ownerId).generation !== expected.generation ||
            copy.generation !== expected.generation
          )
            throw new SnapshotOwnerRemovedError()
          const key = ['recovery-copy', copy.ownerId, copy.operationId]
          const removed = store.get([
            'removed-recovery-copy',
            copy.ownerId,
            copy.operationId,
          ])
          removed.onsuccess = () => {
            if (removed.result !== undefined) {
              fail(new SnapshotConflictError())
              return
            }
            const prior = store.get(key)
            prior.onsuccess = () => {
              try {
                if (
                  prior.result !== undefined &&
                  JSON.stringify(
                    decodeRecovery(
                      prior.result,
                      copy.ownerId,
                      copy.operationId,
                    ),
                  ) !== JSON.stringify(copy)
                )
                  throw new SnapshotConflictError()
                if (prior.result === undefined) store.put(copy, key)
                done(undefined)
              } catch (error) {
                fail(
                  error instanceof Error ? error : new SnapshotStorageError(),
                )
              }
            }
          }
        },
      )
    },
    readRecoveries(ownerId: string): Promise<SnapshotRecoveryCopy[]> {
      return transaction(ownerId, 'readonly', (store, raw, done, fail) => {
        const generation = decode(raw, ownerId).generation
        const copies: SnapshotRecoveryCopy[] = []
        const cursor = store.openCursor()
        cursor.onsuccess = () => {
          try {
            const row = cursor.result
            if (!row) {
              done(copies.sort((a, b) => a.createdAt - b.createdAt))
              return
            }
            if (recoveryKey(row.key, ownerId)) {
              const copy = decodeRecovery(row.value, ownerId, row.key[2]!)
              if (copy.generation !== generation)
                throw new SnapshotStorageError()
              copies.push(copy)
            }
            row.continue()
          } catch (error) {
            fail(error instanceof Error ? error : new SnapshotStorageError())
          }
        }
      })
    },
    removeRecovery(expected: SnapshotRecoveryCopy): Promise<void> {
      const copy = decodeRecovery(
        expected,
        expected.ownerId,
        expected.operationId,
      )
      return transaction(
        copy.ownerId,
        'readwrite',
        (store, raw, done, fail) => {
          if (decode(raw, copy.ownerId).generation !== copy.generation)
            throw new SnapshotOwnerRemovedError()
          const key = ['recovery-copy', copy.ownerId, copy.operationId]
          const prior = store.get(key)
          prior.onsuccess = () => {
            try {
              if (
                prior.result !== undefined &&
                JSON.stringify(
                  decodeRecovery(prior.result, copy.ownerId, copy.operationId),
                ) !== JSON.stringify(copy)
              )
                throw new SnapshotConflictError()
              store.put(true, [
                'removed-recovery-copy',
                copy.ownerId,
                copy.operationId,
              ])
              store.delete(key)
              done(undefined)
            } catch (error) {
              fail(error instanceof Error ? error : new SnapshotStorageError())
            }
          }
        },
      )
    },
    forgetOwner(ownerId: string, operationId: string): Promise<void> {
      if (!identifier(operationId)) throw new SnapshotStorageError()
      return transaction(ownerId, 'readwrite', (store, _raw, done, fail) => {
        const prior = store.get(forgottenKey(ownerId, operationId))
        prior.onsuccess = () => {
          try {
            if (prior.result !== undefined) {
              if (prior.result !== true) throw new SnapshotStorageError()
              done(undefined)
              return
            }
            // Ownership is the explicit key, so corrupt owned bodies can be
            // removed without guessing or decoding their private contents.
            store.put(
              {
                version: 1,
                ownerId,
                generation: operationId,
                revision: crypto.randomUUID(),
                value: null,
              },
              ownerKey(ownerId),
            )
            store.put(true, forgottenKey(ownerId, operationId))
            clearRecoveryCopies(store, ownerId, () => done(undefined))
          } catch (error) {
            fail(error instanceof Error ? error : new SnapshotStorageError())
          }
        }
      })
    },
    deleteOwner(ownerId: string): Promise<void> {
      return transaction(
        ownerId,
        'readwrite',
        (store, _raw, done) => {
          store.put(true, deletedKey(ownerId))
          store.delete(ownerKey(ownerId))
          clearRecoveryCopies(store, ownerId, () => done(undefined))
        },
        true,
      )
    },
  }
}
